import {
  ISendMessagePayload,
  SEND_MESSAGE_TYPE,
  SOCKET_BODY_EMOJI,
} from "@home-health-notify/domain";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";
import { Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { US_PLACES_REGION_CODES, useTranslation } from "hhn-commons";
import React, { useEffect, useState } from "react";
import {
  DEFAULT_MAP_POSITION,
  DEFAULT_ZOOM,
  IAddressData,
  ILocationData,
  SEARCH_MAP_ZOOM,
} from "../../addPatientGroup/addressField/AddressMapHelper";
import AddressMapInput from "../../addPatientGroup/addressField/AddressMapInput";
import AddressMapMarker from "../../addPatientGroup/addressField/AddressMapMarker";
import FullScreenLayout from "../../layout/FullScreenLayout";
import config from "../../utils/Config";
import {
  CloseButton,
  InputWrapper,
  LoadingOverlay,
  ShareLocationContainer,
} from "./ShareLocation.style";

interface ShareLocationProps {
  isOpen: boolean;
  onClose: () => void;
  sendMessage: (
    payload: ISendMessagePayload,
    sendMessageType: SEND_MESSAGE_TYPE,
  ) => void;
}

const mapID = "share-location-map";
const inputID = "share-location-input";

const generateStaticMapUrl = (lat: string, lng: string): string => {
  return `https://maps.googleapis.com/maps/api/staticmap?zoom=15&size=350x350&maptype=roadmap&markers=color:red%7Clabel:S%7C${lat},${lng}&key=${config.googleMapKey}`;
};

const CURRENT_LOCATION_ZOOM = 15;

const ShareLocation = ({
  isOpen,
  onClose,
  sendMessage,
}: ShareLocationProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });

  const map = useMap(mapID);
  const placesLibrary = useMapsLibrary("places");
  const geocodingLibrary = useMapsLibrary("geocoding");

  const [addressData, setAddressData] = useState<IAddressData>({
    ...DEFAULT_MAP_POSITION,
    placeName: "",
  });
  const [mapCenter] = useState<ILocationData>(DEFAULT_MAP_POSITION);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);

  // Get current location on mount
  useEffect(() => {
    if (!isOpen) return;

    // Reset state when modal opens
    setIsLoadingLocation(true);
    setAddressData({ ...DEFAULT_MAP_POSITION, placeName: "" });

    const getCurrentLocation = () => {
      if (!navigator.geolocation) {
        setIsLoadingLocation(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async position => {
          const { latitude, longitude } = position.coords;
          const newLocation: IAddressData = {
            lat: latitude,
            lng: longitude,
            placeName: "",
          };

          // Reverse geocode to get place name
          if (geocodingLibrary) {
            try {
              const geocoder = new (geocodingLibrary.Geocoder as any)();
              const { results } = await geocoder.geocode({
                location: { lat: latitude, lng: longitude },
              });
              if (results[0]) {
                newLocation.placeName = results[0].formatted_address;
              }
            } catch (error) {
              console.error("Reverse geocoding failed:", error);
            }
          }

          setAddressData(newLocation);

          // Center map on current location
          if (map) {
            map.setCenter({ lat: latitude, lng: longitude });
            map.setZoom(CURRENT_LOCATION_ZOOM);
          }

          setIsLoadingLocation(false);
        },
        error => {
          console.error("Error getting location:", error);
          setIsLoadingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000 },
      );
    };

    getCurrentLocation();
  }, [isOpen, geocodingLibrary, map]);

  const onPlaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddressData({
      ...addressData,
      placeName: value,
    });

    if (!geocodingLibrary || !placesLibrary) return;
    const geocoder = new (geocodingLibrary.Geocoder as any)();
    const inputEl = document.getElementById(inputID) as HTMLInputElement;
    const autocomplete = new (placesLibrary.Autocomplete as any)(inputEl, {
      componentRestrictions: { country: US_PLACES_REGION_CODES },
    });
    autocomplete.addListener("place_changed", async () => {
      const place = autocomplete.getPlace();
      if (!place.place_id) {
        return;
      }
      const { results } = await geocoder.geocode({ placeId: place.place_id });
      const position = results[0].geometry.location.toJSON();
      const addressInfo: IAddressData = {
        lat: position.lat,
        lng: position.lng,
        placeName: results[0].formatted_address,
      };
      setAddressData(addressInfo);
      if (map) {
        map.setZoom(SEARCH_MAP_ZOOM);
        map.setCenter(position);
      }
    });
  };

  const onMarkerDrag = async (data: {
    latLng: { toJSON: () => { lat: number; lng: number } };
  }) => {
    if (!geocodingLibrary) return;
    let position = data.latLng.toJSON();
    const geocoder = new (geocodingLibrary.Geocoder as any)();
    const { results } = await geocoder.geocode({ location: position });
    if (results[0]) {
      position = results[0].geometry.location.toJSON();
      const addressInfo: IAddressData = {
        lat: position.lat,
        lng: position.lng,
        placeName: results[0].formatted_address,
      };
      setAddressData(addressInfo);
    }
  };

  const onClearAddress = () => {
    setAddressData({
      ...addressData,
      placeName: "",
    });
  };

  const onShareLocation = () => {
    if (!addressData.lat || !addressData.lng || !addressData.placeName) return;

    const payload: ISendMessagePayload = {
      body: SOCKET_BODY_EMOJI.LOCATION,
      location: {
        lat: String(addressData.lat),
        lng: String(addressData.lng),
        placeName: addressData.placeName,
        mapUrl: generateStaticMapUrl(
          String(addressData.lat),
          String(addressData.lng),
        ),
      },
    };

    sendMessage(payload, SEND_MESSAGE_TYPE.LOCATION);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <FullScreenLayout>
      <ShareLocationContainer>
        <Map
          mapId={config.googleMapId}
          id={mapID}
          style={{ width: "100%", height: "100%" }}
          defaultCenter={mapCenter}
          defaultZoom={DEFAULT_ZOOM}
          gestureHandling="greedy"
          disableDefaultUI={true}
          fullscreenControl={false}
          mapTypeControl={false}
          streetViewControl={false}
        >
          <InputWrapper>
            <AddressMapInput
              addressData={addressData}
              onChange={onPlaceChange}
              onClick={onShareLocation}
              buttonLabel={tNs("share")}
              inputId={inputID}
              placeholder={tNs("searchLocation")}
              onClear={onClearAddress}
            />
          </InputWrapper>
          <AddressMapMarker
            addressData={addressData}
            onDragEnd={onMarkerDrag}
          />
        </Map>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
        {isLoadingLocation && (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        )}
      </ShareLocationContainer>
    </FullScreenLayout>
  );
};

export default ShareLocation;
